import React, { useEffect, useState, useCallback } from 'react';

import { NumericFormat } from 'react-number-format';

import * as Store from '@apply/store';
import * as UI from '@apply/ui';

export interface EntryFieldProps {
    spec: Store.FormsStore.Types.ValueSpec;
    category: string;
    value: Store.FormsStore.Types.FormRecordValueType;
    submitting: boolean;
    printMode?: boolean;
    onChange: (newvalue: Store.FormsStore.Types.FormRecordValueType) => void;
}

export const parseValue = (part: 'value' | 'sidecar', value: Store.FormsStore.Types.FormRecordValueType): string => {
    if (!value) {
        return '';
    } else if (Array.isArray(value) && value.length === 2) {
        if (part === 'value') {
            return value[1] || '';
        } else {
            return value[0] || '';
        }
    } else {
        return value;
    }
};

export const invalidPhoneNumber = (value: string) => {
    if (!value || !value.trim()) {
        return false;
    } else {
        return !UI.validPhone(value);
    }
};

export const EntryField: React.FC<EntryFieldProps> = ({ spec, category, value, submitting, printMode, onChange }) => {
    const [internalValue, setInternalValue] = useState<string>(parseValue('value', value));
    const [internalSidecar, setInternalSidecar] = useState<string>(parseValue('sidecar', value));

    const [middleColumnWidth, setMiddleColumnWidth] = useState<string>('50%');

    const handleSidecarChange = useCallback((e) => {
        setInternalSidecar(e.target.value);
    }, []);

    const handleStringValueChange = useCallback((e) => {
        setInternalValue(e.target.value);
    }, []);

    const handleNumberFormatValueChange = useCallback((values) => {
        const { value } = values;
        setInternalValue(value);
    }, []);

    useEffect(() => {
        const newValue: Store.FormsStore.Types.FormRecordValueType = ['otherdescribe', 'amountwithdescription'].includes(
            spec.datatype
        )
            ? [internalSidecar, internalValue]
            : internalValue;
        if (!Store.FormsStore.Types.areValuesEqual(value, newValue)) {
            onChange && onChange(newValue);
        }
    }, [internalSidecar, internalValue]);

    useEffect(() => {
        switch (spec.datatype) {
            case 'amountwithdescription':
                setMiddleColumnWidth('25%');
                break;
            case 'longstring':
                setMiddleColumnWidth('100%');
                break;
            default:
                setMiddleColumnWidth('50%');
                break;
        }
    }, [spec.datatype]);

    const id = `${category}${spec.name}`.replace(/\W+/g, '');
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                padding: '5px',
                paddingRight: '20px',
                justifyContent: 'space-between',
            }}
            className={'entry_field printable-section'}
        >
            <div
                className='entry_field__label'
                style={{
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'row',
                    width: spec.datatype === 'longstring' ? '50%' : '100%',
                }}
            >
                {spec.name}
                {!!spec.advisory && ` (${spec.advisory})`}
            </div>
            {['otherdescribe'].includes(spec.datatype) && (
                <div
                    style={{
                        paddingLeft: '10px',
                        paddingRight: '10px',
                        width: '50%',
                    }}
                >
                    <input
                        id={`${id}Text`}
                        className='forms-input-field'
                        type='text'
                        data-testid='forms-entry-sidecar'
                        value={internalSidecar}
                        onChange={handleSidecarChange}
                        disabled={printMode}
                    />
                </div>
            )}
            <div
                style={{
                    paddingLeft: '10px',
                    paddingRight: '10px',
                    width: middleColumnWidth,
                }}
            >
                {spec.datatype === 'string' && (
                    <input
                        id={id}
                        type='text'
                        className='forms-input-field'
                        data-testid='forms-entry-string'
                        value={internalValue}
                        onChange={handleStringValueChange}
                        disabled={printMode}
                    />
                )}
                {['amount', 'otherdescribe', 'amountwithdescription'].includes(spec.datatype) && (
                    <NumericFormat
                        id={id}
                        className='forms-input-field'
                        data-testid='forms-entry-amount'
                        value={internalValue}
                        thousandSeparator={true}
                        prefix='$ '
                        placeholder={printMode ? '' : '$ 0'}
                        decimalScale={2}
                        onValueChange={handleNumberFormatValueChange}
                        disabled={printMode}
                    />
                )}
                {spec.datatype === 'tel' && (
                    <NumericFormat
                        id={id}
                        className='forms-input-field'
                        data-testid='forms-entry-tel'
                        format='(###) ###-####'
                        mask='_'
                        placeholder={printMode ? '' : '(212) 555-1212'}
                        value={internalValue}
                        onValueChange={handleNumberFormatValueChange}
                        disabled={printMode}
                    />
                )}
                {spec.datatype === 'longstring' && (
                    <textarea
                        id={id}
                        className='forms-input-field'
                        value={internalValue}
                        data-testid='forms-entry-longstring'
                        style={{ width: '100%' }}
                        onChange={handleStringValueChange}
                        disabled={printMode}
                    />
                )}
                {submitting && internalValue.length < 1 && spec.datatype === 'amount' && !spec.optional && (
                    <div
                        style={{
                            color: 'red',
                            width: '100%',
                            textAlign: 'left',
                        }}
                    >
                        Required (value can be $0)
                    </div>
                )}
                {submitting &&
                    internalValue.length < 1 &&
                    !['amount', 'otherdescribe', 'amountwithdescription'].includes(spec.datatype) &&
                    !spec.optional && (
                        <div
                            style={{
                                color: 'red',
                                width: '100%',
                                textAlign: 'left',
                            }}
                        >
                            Required
                        </div>
                    )}
                {submitting && spec.datatype === 'tel' && invalidPhoneNumber(internalValue) && (
                    <div
                        style={{
                            color: 'red',
                            width: '100%',
                            textAlign: 'left',
                        }}
                    >
                        Invalid Phone Number
                    </div>
                )}
            </div>
            {['amountwithdescription'].includes(spec.datatype) && (
                <>
                    <div
                        style={{
                            paddingLeft: '10px',
                            paddingRight: '10px',
                            width: '75%',
                        }}
                    >
                        <input
                            id={`${id}Text`}
                            className='forms-input-field'
                            type='text'
                            data-testid='forms-entry-sidecar'
                            placeholder={printMode ? '' : 'Description of work...'}
                            value={internalSidecar}
                            onChange={handleSidecarChange}
                            disabled={printMode}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
